import React from 'react';
import PropTypes from 'prop-types';
import ReviewQuoteComponent from './ReviewQuoteComponent';
import { makeConcealable } from '@ami/react/core';
import { Modal } from '@ami/react/core';
import { Quote } from '../quote';
import { QuoteProductConfiguration } from '../../quotes/quoteProductConfiguration';
import QuoteWizardContext from '../../quotes/QuoteWizardContext';
import { DealerGroup } from '../../dealer-groups/dealerGroup';
import { dealerUserService } from '../../dealer-users/dealerUserService';
import { User } from '../../users/user';
import { ProductConfiguration } from '../../products/productConfiguration';
import { makeValidable } from '@ami/react/core';
import { Spinner } from '@ami/react/core';
import { DealerUserFilter } from '../../dealer-users/dealerUserFilter';
import { appConfig } from '@ami/react/core';
import { Team } from '../../teams/team';
import { ProductOption } from '../../products/productOption';
import { ProductOptionFilter } from '../../products/productOptionFilter';
import { QuoteOptInDiscountOutput } from '../entities/QuoteOptInDiscountOutput';

const ValidableReviewQuoteComponent = makeValidable(ReviewQuoteComponent);

class ReviewQuoteContainer extends React.Component {
  constructor(props) {
    super(props);
    this.refreshDealerUsers = this.refreshDealerUsers.bind(this);
    this.validableRef = React.createRef();
  }
  static contextType = QuoteWizardContext;
  static propTypes = {
    quote: PropTypes.instanceOf(Quote).isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration),
    onQuoteUpdated: PropTypes.func.isRequired,
    onQuoteAccepted: PropTypes.func.isRequired,
    onAddCoupler: PropTypes.func.isRequired,
    onAddProduct: PropTypes.func.isRequired,
    onEditProduct: PropTypes.func.isRequired,
    onRemoveProduct: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    onCancelEdit: PropTypes.func.isRequired,
    sharedDocuments: PropTypes.array.isRequired,
    onDownload: PropTypes.func.isRequired,
    documentIdDownloading: PropTypes.string,
    onloadSharedDocuments: PropTypes.func.isRequired,
    optInDiscounts: PropTypes.arrayOf(PropTypes.instanceOf(QuoteOptInDiscountOutput)).isRequired,
    showPricingSpinner: PropTypes.bool.isRequired,
    showReviewAlert: PropTypes.bool.isRequired
  };

  state = {
    dealerGroup: null,
    // Only one network call is made but we maintain two arrays for better organization.
    dealerGroupUsers: [],
    dealerUsers: [],
    //
    amiUsers: [],
    dealerBranchSalesTeam: null,
    productOptions: [],
    productDtoIdToRemove: null,
    customLineItemIdToRemove: null,
    showValidations: false
  };

  async componentDidMount() {
    const { dealerBranchId } = this.props.quote.step1;
    const dealerBranch = this.context.getQuoteDealerBranch();
    const { dealerGroupId } = dealerBranch;
    const state = {
      dealerGroup: await DealerGroup.getById(dealerGroupId),
      productOptions: await ProductOption.search(
        new ProductOptionFilter({ alwaysShowImages: false })
      ),
    };
    if (appConfig.isManagementApp) {
      // Only in the context of the Management App the dealerGroupUsers array is needed.
      state.dealerGroupUsers = await dealerUserService.search(
        new DealerUserFilter({ dealerGroupId })
      );
      state.dealerUsers = this.getBranchDealerUsers(
        state.dealerGroupUsers,
        dealerBranchId
      );
      state.amiUsers = await User.search();
      state.dealerBranchSalesTeam = await Team.getById(
        dealerBranch.salesTeamId
      );
    } else {
      state.dealerUsers = await dealerUserService.search(
        new DealerUserFilter({ dealerBranchId })
      );
    }
    this.setState(state);
  }

  getBranchDealerUsers(dealerGroupUsers, dealerBranchId) {
    return dealerGroupUsers.filter((x) =>
      x.dealerGroups.some(
        (z) =>
          z.authorizedDealerBranches.some(
            (y) => y.dealerBranchId === dealerBranchId
          ) || z.isDealerAdmin
      )
    );
  }

  async refreshDealerUsers() {
    const { dealerBranchId, dealerGroupId } = this.props.quote.step1;
    // Only in the context of the Management App the dealerGroupUsers array is needed.
    const dealerGroupUsers = appConfig.isManagementApp
      ? await dealerUserService.search(new DealerUserFilter({ dealerGroupId }))
      : [];
    const dealerUsers = appConfig.isManagementApp
      ? this.getBranchDealerUsers(dealerGroupUsers, dealerBranchId)
      : await dealerUserService.search(
          new DealerUserFilter({ dealerBranchId })
        );
    this.setState({ dealerGroupUsers, dealerUsers });
  }

  removeCustomLineItem = () => {
    const copy = this.props.quote.clone();
    copy.removeCustomLineItem(this.state.customLineItemIdToRemove);
    this.props.onQuoteUpdated(copy);
  };

  renderRemoveProductConfirmationModal = () => (
    <Modal
      title="Remove Product"
      show={!!this.state.productDtoIdToRemove}
      onShowUpdated={(showModal) => {
        if (!showModal) this.setState({ productDtoIdToRemove: null });
      }}
      okTitle="Yes"
      cancelTitle="No"
      onOk={async () => {
        await this.props.onRemoveProduct(this.state.productDtoIdToRemove);
      }}
      showCancel={true}
    >
      <p>Are you sure you would like remove the product from the quote?</p>
    </Modal>
  );

  renderRemoveCustomLineItemConfirmationModal = () => (
    <Modal
      title="Remove Custom Line Item"
      show={!!this.state.customLineItemIdToRemove}
      onShowUpdated={(showModal) => {
        if (!showModal) this.setState({ customLineItemIdToRemove: null });
      }}
      okTitle="Yes"
      cancelTitle="No"
      onOk={this.removeCustomLineItem}
      showCancel={true}
    >
      <p>
        Are you sure you would like remove the custom line item from the quote?
      </p>
    </Modal>
  );

  render() {
    const {
      quoteProductConfiguration,
      isEditMode,
      quote,
      productConfiguration,
      onQuoteUpdated,
      onQuoteAccepted,
      onAddCoupler,
      onAddProduct,
      onEditProduct,
      submitting,
      onCancelEdit,
      sharedDocuments,
      onDownload,
      documentIdDownloading,
      onloadSharedDocuments,
      optInDiscounts,
      showPricingSpinner,
      showReviewAlert
    } = this.props;

    const {
      showValidations,
      dealerGroup,
      dealerGroupUsers,
      dealerUsers,
      amiUsers,
      dealerBranchSalesTeam,
      productOptions
    } = this.state;

    const dealerBranch = this.context.getQuoteDealerBranch();

    if (!quoteProductConfiguration || !dealerBranch || !dealerGroup) {
      return (
        <div className="container text-center">
          <h4 className="mt-5 mb-2">Loading</h4>
          <Spinner show={true} size="fa-5x" />
        </div>
      );
    }

    return (
      <React.Fragment>
        <ValidableReviewQuoteComponent
          ref={this.validableRef}
          showValidations={showValidations}
          isEditMode={isEditMode}
          quote={quote}
          dealerGroup={dealerGroup}
          dealerBranch={dealerBranch}
          dealerGroupUsers={dealerGroupUsers}
          dealerUsers={dealerUsers}
          amiUsers={amiUsers}
          dealerBranchSalesTeam={dealerBranchSalesTeam}
          productConfiguration={productConfiguration}
          quoteProductConfiguration={quoteProductConfiguration}
          onQuoteUpdated={onQuoteUpdated}
          onQuoteAccepted={() => {
            this.setState({ showValidations: true }, () => {
              if (this.validableRef.current.isValid()) {
                onQuoteAccepted();
              }
            });
          }}
          onAddCoupler={onAddCoupler}
          onAddProduct={onAddProduct}
          onEditProduct={onEditProduct}
          onRemoveProduct={(productDtoIdToRemove) =>
            this.setState({ productDtoIdToRemove })
          }
          onRemoveCustomLineItem={(customLineItemIdToRemove) =>
            this.setState({ customLineItemIdToRemove })
          }
          submitting={submitting}
          onCancelEdit={onCancelEdit}
          onNewDealerUserAssigned={this.refreshDealerUsers}
          productOptions={productOptions}
          sharedDocuments={sharedDocuments}
          onDownload={onDownload}
          documentIdDownloading={documentIdDownloading}
          onloadSharedDocuments={onloadSharedDocuments}
          optInDiscounts={optInDiscounts}
          showPricingSpinner={showPricingSpinner}
          showReviewAlert={showReviewAlert}
        />
        {this.renderRemoveProductConfirmationModal()}
        {this.renderRemoveCustomLineItemConfirmationModal()}
      </React.Fragment>
    );
  }
}

export default makeConcealable(ReviewQuoteContainer);
